<template>
	<BaseTooltip position="below" data-test="fund-tag-wrapper"
		><template #trigger
			><Tag
				:text="fundStatus.text"
				background="default"
				icon-name="dot"
				:class="['company-asset-fund__status', `company-asset-fund__status--${fundStatus.indicator}`]"
				data-test="fund-status-tag"
		/></template>
		<div class="body-sm display-flex flex-direction-column text-align-left gap-25">
			<span
				:class="['display-flex align-items-flex-start', `company-asset-fund__status--${fundStatus.indicator}`]"
				><BaseSvgIcon
					name="dot"
					height="6px"
					width="6px"
					class="mr-50 company-asset-fund__status-margin"
				/><span class="font-weight-bold" data-test="fund-status-description">{{
					fundStatus.description
				}}</span></span
			>
			<div>You can learn more about this fund by viewing the fund detail page</div>
			<BaseCtaLink :link="fundDetailsLink" text="View fund detail page" />
		</div>
	</BaseTooltip>
</template>

<script setup lang="ts">
import { AssetFundAllocation } from 'types/assets';
import { buildOfferingDetailsLink } from '@utils/offerings';
import { computed } from 'vue';
import Tag from '@components/tags/tag-component.vue';

interface Props {
	data: AssetFundAllocation;
}

interface FundStatus {
	text: string;
	description: string;
	indicator: string;
}

const props = defineProps<Props>();

const fundDetailsLink = buildOfferingDetailsLink(props.data.fund.fundriseId ?? '', { target: '_blank' });

const fundStatus = computed((): FundStatus => {
	switch (props.data.fund.offeringAvailabilityStatus?.name) {
		case 'OPEN':
			return {
				text: props.data.fund.name,
				description: 'This fund is open for investment',
				indicator: 'green'
			};
		case 'COMING_SOON':
			return {
				text: props.data.fund.name,
				description: 'This fund is coming soon for investment',
				indicator: 'blue'
			};
		case 'RESERVATION_AVAILABLE':
			return {
				text: props.data.fund.name,
				description: 'This fund has an open investment window',
				indicator: 'slate'
			};
		case 'CLOSED':
		default:
			return {
				text: props.data.fund.name ?? '',
				description: 'This fund is currently closed for investment',
				indicator: 'gray'
			};
	}
});
</script>

<style lang="scss">
@use '../../../styles/constants/colors.scss' as *;
@use '../../../styles/utilities/respond-to.scss' as *;
@use 'sass:map';

.company-asset-fund {
	&__status {
		.svg-icon {
			height: 6px;
			width: 6px;
		}
	}

	&__status--green .svg-icon {
		color: map.get($brand-colors, 'green');
	}

	&__status--slate .svg-icon {
		color: token('data-secondary-dark');
	}

	&__status--blue .svg-icon {
		color: token('data-secondary');
	}

	&__status--gray .svg-icon {
		color: map.get($brand-colors, 'gray-40');
	}

	&__status-margin {
		margin-top: 8px;
		@include respond-to(smo) {
			margin-top: 6px;
		}
	}
}
</style>
